import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCowork } from './hooks/useCowork';
import { useUploads } from './hooks/useUploads';
import { useToasts } from './hooks/useToasts';
import { Composer, type ComposerHandle } from './components/Composer';
import { ContextMeter } from './components/ContextMeter';
import { ModelPicker, modelLabel } from './components/ModelPicker';
import { FilesPanel } from './components/FilesPanel';
import { ArtifactsPanel, type ArtifactView } from './components/ArtifactsPanel';
import { SidePanel, type PanelTab } from './components/SidePanel';
import { Toasts } from './components/Toasts';
import { FilesIcon, SparkleIcon, UploadIcon } from './components/icons';
import { Login, LoginShell, type LoginDenial } from './components/Login';
import { PermissionDialog } from './components/PermissionDialog';
import { ShareDialog } from './components/ShareDialog';
import { ConfirmProvider, useConfirm } from './components/ConfirmDialog';
import { SettingsDialog } from './components/SettingsDialog';
import type { McpConnection } from './components/ConnectionsPanel';
import { Rail } from './components/Rail';
import { Transcript } from './components/Transcript';
import type { AgentPermissionMode, RoomFile, RoomView, UserProfile } from '@shared/protocol';

type Auth =
  | { state: 'checking' }
  | { state: 'out'; denial: LoginDenial }
  | { state: 'in'; user: UserProfile };

/**
 * Which account a turn is billed against. Shown because the difference between
 * a Claude subscription and metered API billing is money, and it is otherwise
 * invisible from inside the app.
 */
const AUTH_LABEL: Record<string, string> = {
  subscription: 'claude.ai',
  'api-key': 'API key',
};

const STATUS_LABEL: Record<string, string> = {
  idle: 'Ready',
  queued: 'Queued',
  thinking: 'Working',
  compacting: 'Compacting context',
  awaiting: 'Waiting for approval',
  error: 'Last turn failed',
};

interface MeResponse {
  user?: UserProfile;
  reason?: string;
  email?: string;
  contact?: string;
}

function useAuth(): Auth {
  const [auth, setAuth] = useState<Auth>({ state: 'checking' });

  useEffect(() => {
    // A redirect back from Google describes what just happened; /api/me
    // describes the session as it stands. The redirect is the more specific
    // of the two, so it wins where they disagree.
    const params = new URLSearchParams(location.search);
    const redirectKind = params.get('login_error') ?? undefined;
    const redirectEmail = params.get('login_email') ?? undefined;
    if (redirectKind) {
      // Keep the address bar clean -- and the address out of it -- once the
      // message has been captured.
      history.replaceState(null, '', location.pathname);
    }

    let cancelled = false;
    fetch('/api/me', { credentials: 'same-origin' })
      .then(async (r) => ({
        ok: r.ok,
        body: (await r.json().catch(() => null)) as MeResponse | null,
      }))
      .then(({ ok, body }) => {
        if (cancelled) return;
        if (ok && body?.user) {
          setAuth({ state: 'in', user: body.user });
          return;
        }
        setAuth({
          state: 'out',
          denial: {
            kind: redirectKind ?? body?.reason ?? 'anonymous',
            email: redirectEmail ?? body?.email,
            contact: body?.contact,
          },
        });
      })
      .catch(() => {
        if (!cancelled) {
          setAuth({
            state: 'out',
            denial: { kind: redirectKind ?? 'anonymous', email: redirectEmail },
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return auth;
}

/**
 * True while files are being dragged over the window.
 *
 * dragenter/dragleave fire for every child element crossed, so a single
 * boolean flickers; counting enters against leaves does not.
 */
function useFileDrag(enabled: boolean, onDrop: (files: File[]) => void): boolean {
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setDragging(false);
      return;
    }
    let depth = 0;
    const hasFiles = (e: DragEvent) => !!e.dataTransfer?.types.includes('Files');

    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth += 1;
      setDragging(true);
    };
    const over = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault(); // without this the browser opens the file instead
      e.dataTransfer!.dropEffect = 'copy';
    };
    const leave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragging(false);
    };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setDragging(false);
      const files = [...(e.dataTransfer?.files ?? [])];
      if (files.length) onDrop(files);
    };

    window.addEventListener('dragenter', enter);
    window.addEventListener('dragover', over);
    window.addEventListener('dragleave', leave);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragover', over);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('drop', drop);
    };
  }, [enabled, onDrop]);

  return dragging;
}

function Workspace() {
  const { state, actions } = useCowork();
  const confirm = useConfirm();
  const connected = state.connection === 'open';
  const roomId = state.room?.id;

  const { uploads, upload, dismiss } = useUploads(actions.showError);
  const { toasts, toast, dismiss: dismissToast } = useToasts();
  const [panel, setPanel] = useState<PanelTab | undefined>();
  const [artifactView, setArtifactView] = useState<ArtifactView | undefined>();
  const composerRef = useRef<ComposerHandle>(null);
  const [referenced, setReferenced] = useState<Set<string>>(() => new Set());

  const referencePath = useCallback((path: string) => {
    composerRef.current?.reference(path);
  }, []);
  const reference = useCallback((file: RoomFile) => referencePath(file.path), [referencePath]);

  /** A top-bar button: opens its tab, or closes the panel if already on it. */
  const togglePanel = useCallback((tab: PanelTab) => {
    setPanel((current) => (current === tab ? undefined : tab));
  }, []);
  const closePanel = useCallback(() => setPanel(undefined), []);

  const openArtifact = useCallback((id: string, version?: number) => {
    setArtifactView({ id, version });
    setPanel('artifacts');
  }, []);

  /** An artifact to open once the switch to its room lands. */
  const openOnArrival = useRef<{ roomId: string; id: string } | undefined>(undefined);

  const openInRoom = useCallback(
    (targetRoomId: string, id: string) => {
      if (targetRoomId === roomId) {
        openArtifact(id);
        return;
      }
      openOnArrival.current = { roomId: targetRoomId, id };
      actions.switchRoom(targetRoomId);
    },
    [roomId, openArtifact, actions],
  );

  // What was open belongs to the room it was opened in -- unless the switch
  // was made to show one of this room's artifacts.
  useEffect(() => {
    const pending = openOnArrival.current;
    if (pending && pending.roomId === roomId) {
      openOnArrival.current = undefined;
      setArtifactView({ id: pending.id });
      setPanel('artifacts');
      return;
    }
    setPanel(undefined);
    setArtifactView(undefined);
  }, [roomId]);

  /**
   * Uploads files into the room, skipping any already there under the same
   * name and size -- that is taken to be the same file, and re-sending it
   * would only make a "(2)" copy.
   *
   * @param insert  also reference each file in the message being written;
   *                true for a drop, false for the panel's own picker
   */
  const attach = useCallback(
    (files: File[], insert: boolean) => {
      if (!roomId) return;
      const reused = (file: RoomFile) => {
        toast(`Already uploaded — using the existing ${file.name}`);
        if (insert) reference(file);
      };

      const fresh: File[] = [];
      for (const f of files) {
        const same = state.files.find((x) => x.name === f.name.normalize('NFC') && x.size === f.size);
        if (same) reused(same);
        else fresh.push(f);
      }
      if (fresh.length === 0) return;

      upload(roomId, fresh, (file, wasReused) => {
        // The server can still find a match someone uploaded a moment ago.
        if (wasReused) reused(file);
        else if (insert) reference(file);
      });
    },
    [roomId, state.files, upload, toast, reference],
  );

  const dropFiles = useCallback((files: File[]) => attach(files, true), [attach]);
  // Only with a room to put them in; otherwise the drop is left to the browser.
  const dragging = useFileDrag(connected && !!roomId, dropFiles);

  const [sharingId, setSharingId] = useState<string | undefined>();

  const [settingsOpen, setSettingsOpen] = useState(false);
  /**
   * Whether this person has registered a Bitbucket key. Only the rail badge
   * uses it -- someone who cannot push should find that out from a quiet mark
   * in the sidebar, not from a failed push halfway through a task.
   */
  const [gitKeyMissing, setGitKeyMissing] = useState(false);

  const checkGitKey = useCallback(async () => {
    try {
      const res = await fetch('/api/git/key', { credentials: 'same-origin' });
      if (!res.ok) return;
      const body = (await res.json()) as { available?: boolean; key?: unknown };
      // A server with key storage switched off is not the same as a person
      // who has not got round to it; only the second is worth nagging about.
      setGitKeyMissing(body.available !== false && !body.key);
    } catch {
      /* the badge is a nicety; a failed check just leaves it off */
    }
  }, []);

  useEffect(() => {
    void checkGitKey();
  }, [checkGitKey]);

  /** Same idea as the key badge: a quiet mark when a connection has died. */
  const [connectionBroken, setConnectionBroken] = useState(false);
  const noteConnections = useCallback((list: McpConnection[]) => {
    setConnectionBroken(list.some((c) => c.connected && c.broken));
  }, []);

  useEffect(() => {
    fetch('/api/mcp/connections', { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : undefined))
      .then((body?: { connections?: McpConnection[] }) => body?.connections && noteConnections(body.connections))
      .catch(() => {
        /* the badge is a nicety */
      });
  }, [noteConnections]);

  // Tracked by id, not by value: the room list is re-sent on every sharing
  // change, so holding the object would leave the dialog showing the member
  // list as it was when it opened.
  const sharing = state.rooms.find((r) => r.id === sharingId);

  const createRoom = useCallback(() => {
    // The server gives the room its own folder; there is no path to ask for.
    const name = window.prompt('Room name');
    if (!name?.trim()) return;
    actions.createRoom(name.trim());
  }, [actions]);

  const status = state.status;
  const typingIds = useMemo(
    () => new Set(state.participants.filter((p) => p.typingSince).map((p) => p.id)),
    [state.participants],
  );

  return (
    <div className="shell">
      <Rail
        me={state.me}
        onOpenSettings={() => setSettingsOpen(true)}
        gitKeyMissing={gitKeyMissing}
        connectionBroken={connectionBroken}
        rooms={state.rooms}
        currentRoomId={state.room?.id}
        participants={state.participants}
        onSwitchRoom={actions.switchRoom}
        onCreateRoom={createRoom}
        onManageRoom={(room: RoomView) => setSharingId(room.id)}
      />

      <main className="main">
        <header className="topbar">
          <span className={`dot dot--${status}`} aria-hidden />
          <span className="topbar__title">{state.room?.name ?? 'Loading…'}</span>
          {state.room?.cwd && (
            <span className="topbar__cwd" title={state.room.cwd}>
              {state.room.cwd}
            </span>
          )}

          <span className="topbar__spacer" />

          <span className={`chip ${connected ? 'chip--live' : 'chip--offline'}`}>
            {connected ? STATUS_LABEL[status] ?? status : 'Offline'}
          </span>

          {state.room?.authSource && AUTH_LABEL[state.room.authSource] && (
            <span
              className="chip"
              title={
                state.room.authSource === 'subscription'
                  ? 'Billed against the Claude subscription this server is logged into'
                  : 'Billed per token against an Anthropic API key'
              }
            >
              {AUTH_LABEL[state.room.authSource]}
            </span>
          )}

          {state.totalCostUsd > 0 && (
            <span
              className="chip chip__mono"
              title={
                state.room?.authSource === 'subscription'
                  ? 'Estimated token cost. On a subscription this is a usage figure, not a bill.'
                  : 'Estimated session cost'
              }
            >
              ${state.totalCostUsd.toFixed(3)}
            </span>
          )}

          <ContextMeter
            context={state.context}
            canCompact={connected && (status === 'idle' || status === 'error')}
            onCompact={() => actions.prompt('/compact')}
          />

          <select
            className="mode-select"
            value={state.room?.permissionMode ?? 'default'}
            onChange={(e) => actions.setMode(e.target.value as AgentPermissionMode)}
            title="Permission mode — new rooms you create start with your last pick"
            aria-label="Permission mode"
          >
            <option value="default">Ask before writes</option>
            <option value="auto">Auto — no prompts, risky actions blocked</option>
            {/* Only for a room still set to the old mode, so the select can show it. */}
            {state.room?.permissionMode === 'acceptEdits' && (
              <option value="acceptEdits">Auto-accept edits (old)</option>
            )}
            <option value="plan">Plan only</option>
          </select>

          {state.room?.model && (
            <ModelPicker
              model={state.room.model}
              effort={state.room.effort}
              models={state.models}
              canManage={state.room.canManage}
              onEffort={actions.setEffort}
              onModel={async (next) => {
                const current = state.room?.model;
                // The prompt cache is per model: the first turn after a
                // switch re-reads the whole conversation at full price.
                // A session id means there is one, even if a restart
                // emptied the transcript on screen.
                if (state.room?.sessionId) {
                  const ok = await confirm({
                    title: `Switch this room to ${modelLabel(next)}?`,
                    body:
                      'Claude re-reads the whole conversation so far on the next message. ' +
                      'That turn is slower and uses more of the budget than a normal one.',
                    detail: current ? `${modelLabel(current)} → ${modelLabel(next)}` : undefined,
                    confirmLabel: 'Switch model',
                  });
                  if (!ok) return;
                }
                actions.setModel(next);
              }}
            />
          )}

          <button
            className="topbar__files"
            aria-pressed={panel === 'artifacts'}
            aria-label={`Artifacts (${state.artifacts.length})`}
            title="Artifacts"
            onClick={() => togglePanel('artifacts')}
            disabled={!roomId}
          >
            <SparkleIcon size={16} />
            {state.artifacts.length > 0 && (
              <span className="topbar__files-count">{state.artifacts.length}</span>
            )}
          </button>

          <button
            className="topbar__files"
            aria-pressed={panel === 'files'}
            aria-label={`Files (${state.files.length})`}
            title="Files"
            onClick={() => togglePanel('files')}
            disabled={!roomId}
          >
            <FilesIcon size={16} />
            {(state.files.length > 0 || uploads.length > 0) && (
              <span className="topbar__files-count">
                {state.files.length + uploads.filter((u) => !u.error).length}
              </span>
            )}
          </button>
        </header>

        <Transcript
          entries={state.entries}
          truncated={state.truncated}
          roomName={state.room?.name ?? ''}
          drafts={state.drafts}
          typingIds={typingIds}
          artifacts={state.artifacts}
          onOpenArtifact={openArtifact}
        />

        <Composer
          status={status}
          queueDepth={state.queueDepth}
          connected={connected}
          error={state.error}
          me={state.me}
          participants={state.participants}
          onSend={actions.prompt}
          commands={state.commands}
          onInterrupt={actions.interrupt}
          onTyping={actions.typing}
          onDraft={actions.draft}
          uploads={uploads}
          onDismissUpload={dismiss}
          onReferencesChange={setReferenced}
          ref={composerRef}
          onDismissError={actions.dismissError}
        />
      </main>

      {panel && state.room && (
        <SidePanel
          tab={panel}
          onTab={setPanel}
          counts={{ files: state.files.length, artifacts: state.artifacts.length }}
          wide={panel === 'artifacts' && !!artifactView && state.artifacts.some((a) => a.id === artifactView.id)}
          onClose={closePanel}
        >
          {panel === 'files' ? (
            <FilesPanel
              roomId={state.room.id}
              roomName={state.room.name}
              files={state.files}
              uploads={uploads}
              onUpload={(files) => attach(files, false)}
              onDismissUpload={dismiss}
              onReference={reference}
              referenced={referenced}
              onError={actions.showError}
            />
          ) : (
            <ArtifactsPanel
              roomId={state.room.id}
              artifacts={state.artifacts}
              view={artifactView}
              onView={setArtifactView}
              onReference={referencePath}
              referenced={referenced}
              onError={actions.showError}
            />
          )}
        </SidePanel>
      )}

      {dragging && state.room && (
        <div className="dropzone" aria-hidden>
          <div className="dropzone__card">
            <UploadIcon size={28} />
            <strong>Drop to attach to your message</strong>
            <span>Uploaded to “{state.room.name}” and referenced where your cursor is.</span>
          </div>
        </div>
      )}

      <Toasts toasts={toasts} onDismiss={dismissToast} />

      {state.permission && (
        <PermissionDialog request={state.permission} me={state.me} onDecide={actions.decide} />
      )}

      {sharing && (
        <ShareDialog
          room={sharing}
          meEmail={state.me?.email}
          onShare={(email) => actions.shareRoom(sharing.id, email)}
          onUnshare={(email) => actions.unshareRoom(sharing.id, email)}
          onVisibility={(v) => actions.setRoomVisibility(sharing.id, v)}
          onDelete={() => {
            actions.deleteRoom(sharing.id);
            setSharingId(undefined);
          }}
          onClose={() => setSharingId(undefined)}
        />
      )}

      {settingsOpen && (
        <SettingsDialog
          artifactsKey={state.artifacts}
          onOpenArtifactInRoom={(targetRoomId, id) => {
            setSettingsOpen(false);
            void checkGitKey();
            openInRoom(targetRoomId, id);
          }}
          onError={actions.showError}
          gitKeyMissing={gitKeyMissing}
          connectionBroken={connectionBroken}
          onConnectionsChange={noteConnections}
          onClose={() => {
            setSettingsOpen(false);
            // The badge in the rail is the only thing outside the dialog that
            // depends on this, so re-read it rather than thread state back out.
            void checkGitKey();
          }}
        />
      )}
    </div>
  );
}

export function App() {
  const auth = useAuth();

  if (auth.state === 'checking') return <LoginShell />;
  if (auth.state === 'out') {
    return (
      <LoginShell>
        <Login denial={auth.denial} />
      </LoginShell>
    );
  }
  return (
    <ConfirmProvider>
      <Workspace />
    </ConfirmProvider>
  );
}
